import { EmptyState, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import { isAxiosError } from 'axios';
import classnames from 'classnames';
import { ApiErrorStatusCode } from 'lib-shared-auth';
import {
  Conversation as ConversationT,
  MetaConversation,
  Tag,
} from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import { Conversation as ConversationType, User } from 'lib-shared-types-api';
import _isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useMountedState } from 'react-use';

import {
  getConversationTitle,
  getHumanConversationChannel,
} from 'app/utils/conversationTitle';
import {
  ConversationContextProvider,
  ConversationStatusColorBox,
  ConversationTimelineDetails,
} from 'components/conversation';
import { RestrictedConversation } from 'components/conversation/components/RestrictedConversation';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useConversationSidebarSettings } from 'utils/useConversationSidebarSettings';
import { useServerStorage } from 'utils/useServerStorage';

import Timeline from '../containers/timeline';

import { ConversationSummary } from './conversationSummary';
import { ConversationUnlinkButton } from './conversationUnlinkButton/conversationUnlinkButton';
import { ConversationHeader } from './header';
import { ConversationSidebar } from './sidebar/conversationSidebar';
import { SubConversationList } from './subConversationList';
import { SubConversationListItem } from './subConversationList/subConversationList';

import '../styles/conversation.less';

export type ConversationProps = {
  metaConversation: MetaConversation;
  latestMessengerConversation: any;
  conversationUnmount: any;
  conversationResetUnread: (payload: any) => void;
  conversationGet: (payload: any) => Promise<any>;
  notificationTabBadgeReset: () => void;
  isFocusMode: boolean;
};

const getConversationRequesterDetails = (requester: User | null) =>
  requester?.name || requester?.email || requester?.phone_number || undefined;

function Conversation(props: ConversationProps): JSX.Element {
  const navigate = useAppNavigate();
  const params = useAppPathParams('/conversation/:csid');
  const csid: number = parseInt(params.csid, 10);

  const { data, set } = useServerStorage('user');
  const { isSidebarOpen, setSidebarOpen } = useConversationSidebarSettings();
  const isTimelineExpanded = data.conversation?.isTimelineExpanded ?? false;

  const [error, setError] = useState<'restricted' | 'failed' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMounted = useMountedState();

  const {
    metaConversation,
    isFocusMode,
    conversationResetUnread,
    conversationGet,
    notificationTabBadgeReset,
    conversationUnmount,
  } = props;
  const {
    parentConversation,
    parentConversationRequester,
    linkedConversations,
    conversation,
    requester,
  } = metaConversation;

  const isConversationLoaded = conversation.id === csid;

  const toggleTimeline = useCallback(
    () =>
      set({
        conversation: (draft) => {
          draft.isTimelineExpanded = !draft.isTimelineExpanded;
        },
      }),
    [set],
  );

  const fetchConversation = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      await conversationGet({ csid });

      if (!isMounted()) return;

      setIsLoading(false);
      conversationResetUnread({ csid });
      notificationTabBadgeReset();
    } catch (err) {
      if (!isMounted()) return;

      setIsLoading(false);

      if (
        isAxiosError(err) &&
        err.response?.status === ApiErrorStatusCode.Forbidden
      ) {
        setError('restricted');
      } else {
        setError('failed');
        Sentry.captureException(err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csid]);

  useEffect(() => {
    fetchConversation();

    return () => conversationUnmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchConversation]);

  if (error === 'restricted') {
    return <RestrictedConversation />;
  }

  if (error === 'failed') {
    return (
      <FlexBox
        flexDirection="column"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <EmptyState
          title="Something went wrong"
          description="We're experiencing some issues, try again or reload the page. If this issue persists, please contact support."
          icon="exclamation"
          iconColor="red.500"
          buttonProps={{
            children: 'Try again',
            onClick: fetchConversation,
          }}
        />
      </FlexBox>
    );
  }

  if (isLoading || !isConversationLoaded) {
    return (
      <FlexBox
        flexDirection="column"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </FlexBox>
    );
  }

  /**
   * Related link type is considered to be a same level link (as opposed to
   * parent-child relation). And should allow to display a timeline.
   */
  const isRelatedConversation = conversation.link_type === 'related';

  return (
    <ConversationContextProvider conversation={metaConversation.conversation}>
      <div className="conversation-view">
        {(!parentConversation || isRelatedConversation) && (
          <div
            className={classnames('conversation-view__timeline print-hidden', {
              'conversation-view__timeline--placeholder': !!parentConversation,
              'conversation-view__focus-mode': isFocusMode,
              'conversation-view__timeline--details': isTimelineExpanded,
            })}
          >
            {isTimelineExpanded ? (
              <ConversationTimelineDetails
                csid={conversation.id as ConversationType['id']}
                requesterId={requester?.id as string}
                handleToggleTimeline={toggleTimeline}
              />
            ) : (
              <Timeline handleToggleTimeline={toggleTimeline} />
            )}
          </div>
        )}
        <div className="conversation-view__main">
          {!!parentConversation && (
            <div
              className={classnames('conversation-view__parent print-hidden', {
                'conversation-view__parent__related': isRelatedConversation,
              })}
              onClick={() =>
                navigate({
                  path: '/conversation/:csid',
                  params: {
                    path: {
                      csid: `${parentConversation.id}`,
                    },
                  },
                })
              }
              data-status={parentConversation.status}
            >
              <ConversationStatusColorBox
                className="conversation-status"
                csid={parentConversation.id as ConversationType['id']}
                status={parentConversation.status as ConversationType['status']}
              />

              <ConversationSummary
                className="conversation-view__parent__summary"
                conversationAttributes={
                  metaConversation.conversation.attributes
                }
                conversationInfo={parentConversation}
                labels={{
                  title: getConversationTitle({
                    conversation: parentConversation,
                    requester: parentConversationRequester,
                  }),
                  agent: conversation.assignment?.agent?.name || undefined,
                  requester: getConversationRequesterDetails(
                    parentConversationRequester || null,
                  ),
                  queue: parentConversation.queue?.name,
                  channel: getHumanConversationChannel(
                    parentConversation,
                  ) as any,
                }}
              />
              <FlexBox
                flex="1"
                justifyContent="flex-end"
                alignItems="center"
                margin="0 200"
              >
                <ConversationUnlinkButton />
              </FlexBox>
            </div>
          )}
          <div
            data-status={metaConversation.conversation.status}
            className="conversation-view__current conversation-messages"
          >
            <ConversationHeader />
            <Outlet />
          </div>
          {!parentConversation &&
            linkedConversations &&
            linkedConversations.length > 0 && (
              <div className="conversation-view__children print-hidden">
                <SubConversationList
                  conversations={linkedConversations.map<SubConversationListItem>(
                    (metaC: {
                      conversation: ConversationT;
                      notes: Array<any>;
                      requester: User;
                      tags: Array<Tag>;
                    }) => {
                      return {
                        info: metaC.conversation,
                        requester: metaC.requester,
                        labels: {
                          title: getConversationTitle(metaC),
                          agent: metaC.conversation.assignment?.agent?.name,
                          queue: metaC.conversation.queue?.name,
                          channel: getHumanConversationChannel(
                            metaC.conversation,
                          ),
                        },
                      } as SubConversationListItem;
                    },
                  )}
                  onConversationClick={(csid: number | string) =>
                    navigate({
                      path: '/conversation/:csid',
                      params: {
                        path: {
                          csid: `${csid}`,
                        },
                      },
                    })
                  }
                />
              </div>
            )}
        </div>
        <div
          className={classnames('conversation-view__sidebar print-hidden', {
            closed: !isSidebarOpen,
          })}
        >
          <ConversationSidebar />
        </div>

        <div
          className={classnames('conversation-view__backdrop print-hidden', {
            closed: !isSidebarOpen,
          })}
          onClick={() => setSidebarOpen(false)}
        />
      </div>
    </ConversationContextProvider>
  );
}

export default memo(Conversation, _isEqual);
